import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Shield, Zap, Lock, ArrowLeft, Loader2, ChevronRight, Sparkles, Construction, Heart, Palette, Flame, Gift, Snowflake, Target, CheckCircle2, Trophy, UserRoundCheck, Eye, Palmtree } from "lucide-react";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";
import SharpEyesCelebration from "@/components/SharpEyesCelebration";
import FloatingCalculator from "@/components/Calculator";
import { subjectIcon, isAvailable, visibleSubjects, subjectLabel } from "@/lib/subjects";
import HolidayCountdown from "@/components/HolidayCountdown";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";


export default function KidHome() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [streak, setStreak] = useState(0);
  const [showFreezeInfo, setShowFreezeInfo] = useState(false);
  const [milestone, setMilestone] = useState(0);

  useEffect(() => {
    let active = true;
    api.get(`/children/${childId}/overview`).then(({ data }) => {
      if (!active) return;
      setData(data);
      setStreak(data.child.streak_days || 0);
      if (data.child.blocked) return;
      // daily streak check-in: rewards coming back to practise
      api.post(`/children/${childId}/checkin`).then(({ data: ci }) => {
        if (!active) return;
        setStreak(ci.streak_days);
        setData((d) => d ? { ...d, child: { ...d.child, streak_days: ci.streak_days, streak_freezes: ci.streak_freezes, effort_points: ci.child.effort_points, activity: ci.child.activity } } : d);
        if (ci.froze) {
          toast(`Phew! A streak freeze saved your ${ci.streak_days}-day streak 🔥`, { icon: "❄️" });
        } else if (ci.new_day && ci.awarded > 0) {
          toast.success(`Day ${ci.streak_days} streak! 🔥 +${ci.awarded} effort points`);
        }
        if (ci.earned_freeze) {
          const key = `bb_freeze_explained_${childId}`;
          let explained = false;
          try { explained = localStorage.getItem(key) === "1"; } catch { /* ignore */ }
          if (!explained) {
            setShowFreezeInfo(true);
            try { localStorage.setItem(key, "1"); } catch { /* ignore */ }
          } else {
            toast.success("You earned a Streak Freeze ❄️ — it saves your flame if you miss a day!");
          }
        }
        if (ci.milestone) setMilestone(ci.milestone);
      }).catch(() => {});
    }).catch(() => navigate("/parent"));
    return () => { active = false; };
  }, [childId, navigate]);

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#EAF7F4]"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;
  }

  const { child, subjects, plan_active } = data;
  const readingOnly = Boolean(data.reading_only);

  if (child.blocked) {
    return (
      <div className="min-h-screen kid-bg font-kids text-slate-900 flex items-center justify-center px-5">
        <div className="nb-card bg-white p-8 text-center max-w-md" data-testid="kid-paused">
          <div className="mx-auto w-16 h-16 rounded-full border-4 border-slate-900 bg-amber-100 flex items-center justify-center" style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
            <Lock className="text-amber-600" size={30} />
          </div>
          <h1 className="text-2xl font-bold mt-4">Learning is paused</h1>
          <p className="font-parent text-slate-500 mt-2">{child.name}'s learning is paused right now. Please ask a parent or your administrator to resume it.</p>
          <Button onClick={() => navigate("/parent")} className="mt-5 rounded-full bg-blue-600 hover:bg-blue-700 border-2 border-slate-900 h-11 px-6 font-parent font-semibold" data-testid="kid-paused-back">Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen kid-bg font-kids text-slate-900">
      <SharpEyesCelebration count={child.sharp_eyes_pending || 0} total={child.sharp_eyes || 0} onClose={() => {
        api.post(`/children/${childId}/sharp-eyes/seen`).catch(() => {});
        setData((d) => ({ ...d, child: { ...d.child, sharp_eyes_pending: 0 } }));
      }} />
      <div aria-hidden className="absolute inset-x-0 top-0 h-72 sky-bg rounded-b-[48px]" />
      <AnimatePresence>
        {showFreezeInfo && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowFreezeInfo(false)}
            data-testid="freeze-explainer"
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-6">
            <motion.div
              initial={{ scale: 0.6, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 18 }}
              onClick={(e) => e.stopPropagation()}
              className="nb-card bg-white p-7 text-center max-w-sm w-full">
              <motion.div
                animate={{ rotate: [0, -12, 12, 0], scale: [1, 1.1, 1] }}
                transition={{ repeat: Infinity, duration: 3 }}
                className="mx-auto w-20 h-20 rounded-full border-4 border-slate-900 bg-sky-100 flex items-center justify-center"
                style={{ boxShadow: "4px 4px 0 0 #0F172A" }}>
                <Snowflake className="text-sky-500" size={40} />
              </motion.div>
              <h2 className="font-kids text-2xl font-bold text-slate-900 mt-4">You got a Streak Freeze! ❄️</h2>
              <p className="font-parent text-slate-600 mt-2 text-sm leading-relaxed">
                Keep practising every day to grow your <Flame className="inline text-orange-500" size={15} /> streak.
                If you miss <span className="font-bold">one day</span>, a Streak Freeze is used automatically to <span className="font-bold">save your flame</span> — so your streak keeps going!
              </p>
              <Button onClick={() => setShowFreezeInfo(false)} data-testid="freeze-explainer-close"
                className="mt-5 rounded-full bg-slate-900 hover:bg-slate-800 text-white h-12 px-8 border-2 border-slate-900 font-parent font-semibold">
                Got it!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {milestone > 0 && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setMilestone(0)}
            data-testid="streak-milestone"
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-6">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(36)].map((_, i) => (
                <motion.div key={i}
                  initial={{ y: -40, x: `${Math.random() * 100}%`, opacity: 1, rotate: 0 }}
                  animate={{ y: "110vh", rotate: 360 + Math.random() * 360 }}
                  transition={{ duration: 1.8 + Math.random() * 1.2, delay: Math.random() * 0.5, ease: "easeIn" }}
                  className="absolute top-0"
                  style={{ width: 10, height: 14, background: ["#F97316", "#FACC15", "#EF4444", "#10B981", "#3B82F6"][i % 5], borderRadius: 2 }} />
              ))}
            </div>
            <motion.div
              initial={{ scale: 0.5, rotate: -8, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 16 }}
              onClick={(e) => e.stopPropagation()}
              className="relative nb-card bg-white p-8 text-center max-w-sm w-full">
              <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ repeat: Infinity, duration: 1.6 }}
                className="mx-auto w-24 h-24 rounded-full border-4 border-slate-900 bg-orange-100 flex items-center justify-center" style={{ boxShadow: "4px 4px 0 0 #0F172A" }}>
                <Flame className="text-orange-500" size={52} fill="currentColor" />
              </motion.div>
              <p className="font-parent font-bold text-orange-600 text-sm uppercase tracking-wide mt-4 flex items-center justify-center gap-1"><Trophy size={16} /> Streak milestone!</p>
              <h2 className="font-kids text-4xl font-bold text-slate-900 mt-1">{milestone} days! 🔥</h2>
              <p className="font-parent text-slate-600 mt-2 text-sm">
                {milestone >= 30 ? "A whole month of learning — you're unstoppable!" : "One week in a row — you're on fire!"} You earned a <span className="font-bold">+50 effort</span> bonus!
              </p>
              <Button onClick={() => setMilestone(0)} data-testid="streak-milestone-close"
                className="mt-5 rounded-full bg-slate-900 hover:bg-slate-800 text-white h-12 px-8 border-2 border-slate-900 font-parent font-semibold">
                Keep it up!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <header className="relative max-w-5xl mx-auto px-5 pt-6 flex items-center justify-between">
        <button onClick={() => navigate("/parent")} data-testid="kid-back"
          className="inline-flex items-center gap-2 rounded-full border-4 border-slate-900 bg-white px-4 py-2 font-parent font-bold text-slate-800 hover:-translate-y-0.5 transition-transform"
          style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
          <UserRoundCheck size={18} className="text-blue-600" /> Ask a parent
        </button>
        <button
          onClick={() => navigate(`/kid/${childId}/collection`)}
          data-testid="kid-collection-btn"
          className="relative inline-flex items-center gap-2 rounded-full border-4 border-slate-900 bg-pink-400 px-4 py-2 font-parent font-bold text-white"
          style={{ boxShadow: "3px 3px 0 0 #0F172A" }}
        >
          <Palette size={18} /> My Collection
          {child.available_unlocks > 0 && (
            <span className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1 rounded-full bg-amber-300 border-2 border-slate-900 text-slate-900 text-xs font-bold flex items-center justify-center" data-testid="kid-unlock-badge">
              {child.available_unlocks}
            </span>
          )}
        </button>
      </header>

      {/* Hero */}
      <div className="relative max-w-5xl mx-auto px-5 pt-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="nb-card bg-white p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6">
          <motion.button animate={{ rotate: [0, -6, 6, 0] }} transition={{ repeat: Infinity, duration: 4 }}
            onClick={() => navigate(`/kid/${childId}/collection`)} data-testid="kid-avatar-btn" title="Change avatar">
            <Avatar avatar={child.avatar} img={child.avatar_img} size={96} testId="kid-avatar" />
          </motion.button>
          <div className="text-center sm:text-left flex-1">
            <p className="text-sm font-parent text-slate-500">Bonzour,</p>
            <h1 className="text-3xl sm:text-4xl font-bold">{child.name}!</h1>
            <p className="font-parent text-slate-500 mt-1">Grade {child.grade} · Anou aprann kiksoz nouvo zordi</p>
          </div>
          <div className="flex gap-3 flex-wrap justify-center">
            <Badge icon={Flame} color="#F97316" value={streak} testId="kid-streak-badge" />
            {(child.streak_freezes || 0) > 0 && (
              <Badge icon={Snowflake} color="#38BDF8" value={child.streak_freezes} testId="kid-freeze-badge" />
            )}
            <Badge icon={Star} color="#EAB308" value={child.stars} />
            <Badge icon={Shield} color="#3B82F6" value={child.shields} />
            <Badge icon={Heart} color="#EC4899" value={child.effort_points || 0} testId="kid-effort-badge" />
            <Badge icon={Zap} color="#10B981" value={child.xp} />
            {(child.sharp_eyes || 0) > 0 && (
              <Badge icon={Eye} color="#14B8A6" value={`Sharp Eyes${child.sharp_eyes > 1 ? ` ×${child.sharp_eyes}` : ""}`} testId="kid-sharp-eyes-badge" />
            )}
          </div>
        </motion.div>
      </div>

      {/* Streak calendar */}
      <div className="relative max-w-5xl mx-auto px-5 pt-4">
        <StreakCalendar activity={child.activity || []} streak={streak} />
      </div>

      {/* Weekly challenge */}
      {child.weekly && !readingOnly && (
        <div className="max-w-5xl mx-auto px-5 pt-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            data-testid="kid-weekly-challenge"
            className={`nb-card p-4 sm:p-5 flex items-center gap-4 ${child.weekly.done ? "bg-green-300" : "bg-white"}`}>
            <div className="w-12 h-12 rounded-full border-4 border-slate-900 bg-white flex items-center justify-center shrink-0" style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
              {child.weekly.done ? <CheckCircle2 className="text-green-600" size={26} /> : <Target className="text-blue-600" size={24} />}
            </div>
            <div className="flex-1">
              <p className="font-kids text-lg sm:text-xl font-bold text-slate-900">Weekly Challenge</p>
              {child.weekly.done ? (
                <p className="font-parent text-sm text-slate-800">Done this week! You earned a bonus avatar unlock 🎁 New challenge Monday.</p>
              ) : (
                <p className="font-parent text-sm text-slate-600">
                  Master <span className="font-bold text-slate-900">1 chapter</span> (80%+ on all 3 levels) this week to earn a bonus avatar unlock!
                </p>
              )}
            </div>
            <div className="font-kids text-xl font-bold text-slate-900 shrink-0">
              {Math.min(child.weekly.mastered, child.weekly.target)}/{child.weekly.target}
            </div>
          </motion.div>
        </div>
      )}

      {/* Almost there nudge — one mastered chapter from the next avatar */}
      {!readingOnly && child.available_unlocks === 0 && child.next_unlock_in === 1 && (
        <div className="max-w-5xl mx-auto px-5 pt-4">
          <motion.button
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            onClick={() => navigate(`/kid/${childId}/collection`)}
            data-testid="kid-almost-nudge"
            className="w-full nb-card bg-amber-300 p-4 sm:p-5 flex items-center gap-4 text-left">
            <div className="w-12 h-12 rounded-full border-4 border-slate-900 bg-white flex items-center justify-center shrink-0" style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
              <Gift className="text-amber-600" size={24} />
            </div>
            <div className="flex-1">
              <p className="font-kids text-lg sm:text-xl font-bold text-slate-900">Almost there! 🎁</p>
              <p className="font-parent text-sm text-slate-700">Master just <span className="font-bold">1 more chapter</span> (80%+ on all 3 levels) to unlock a brand-new avatar!</p>
            </div>
            <ChevronRight className="text-slate-700 shrink-0" size={22} />
          </motion.button>
        </div>
      )}

      {/* Subjects map */}
      <div className="max-w-5xl mx-auto px-5 py-8">
        <div className="flex items-center justify-between gap-3 mb-5">
          <h2 className="text-2xl font-bold" data-testid="kid-home-title">{readingOnly ? "Let's read! · On lit !" : "Pick your adventure"}</h2>
          <button onClick={() => navigate("/rewards")} className="rounded-full bg-white border-2 border-slate-900 px-4 py-1.5 text-sm font-bold hover:bg-yellow-50 transition-colors" data-testid="kid-rewards-link">How to earn rewards</button>
        </div>
        {data.has_access === false && !readingOnly && <DemoBanner onStart={() => navigate(`/kid/${childId}/demo`)} />}
        <motion.button onClick={() => navigate(`/kid/${childId}/reading`)} whileHover={{ y: -3 }} whileTap={{ scale: 0.98 }} data-testid="kid-reading-tile"
          className="nb-card w-full text-left bg-gradient-to-r from-green-200 to-yellow-200 p-5 mb-6 flex items-center gap-4">
          <motion.img src="/mascot/kiko.png" alt="Kiko" className="w-20 h-20 shrink-0" animate={{ rotate: [0, -8, 8, 0] }} transition={{ repeat: Infinity, duration: 2.5 }} />
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-wider text-green-800">New · Nouveau</p>
            <h3 className="text-2xl font-bold">Reading with Kiko · Lecture avec Kiko</h3>
            <p className="font-parent text-sm text-slate-700">Sounds, words and stories in English and French 🔤</p>
          </div>
          <span className="text-3xl">📖</span>
        </motion.button>
        {child.holiday_until && new Date(child.holiday_until) > new Date() && (
          <div className="nb-card bg-orange-100 p-5 mb-6" data-testid="kid-holiday-banner">
            <p className="text-xl font-black flex items-center gap-2"><Palmtree className="text-orange-500" /> Holiday Pack / Révisions de vacances</p>
            {child.holiday_from && new Date(child.holiday_from) > new Date() ? (
              <>
                <p className="font-parent text-sm text-slate-700 mt-1" data-testid="kid-holiday-soon">Your spot is booked! The pack opens on 15 November. / Ta place est réservée ! Le pack ouvre le 15 novembre.</p>
                <HolidayCountdown childId={childId} from={child.holiday_from} until={child.holiday_until} />
              </>
            ) : (
              <>
                <p className="font-parent text-sm text-slate-700 mt-1">One question of each subject in turn, plus sneak peeks at next year. Finish the whole pack before 15 January to win 3 special avatars! / Une question de chaque matière à tour de rôle. Termine tout le pack avant le 15 janvier pour gagner 3 avatars !</p>
                <HolidayCountdown childId={childId} from={child.holiday_from} until={child.holiday_until} />
                <button onClick={() => navigate(`/kid/${childId}/holiday`)} data-testid="kid-holiday-start"
                  className="mt-3 rounded-full bg-orange-500 text-white border-2 border-slate-900 px-6 py-2.5 font-bold hover:bg-orange-600 shadow-[2px_2px_0_0_#0F172A]">Start my holiday revision</button>
              </>
            )}
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visibleSubjects(readingOnly ? [] : subjects, child.grade).map((s, i) => {
            const Icon = subjectIcon(s.name);
            const comingSoon = !isAvailable(child.grade, s.name, s);
            const locked = !s.unlocked;
            return (
              <motion.button
                key={s.name}
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 18, delay: i * 0.06 }}
                whileHover={comingSoon ? {} : { y: -4 }} whileTap={comingSoon ? {} : { scale: 0.97 }}
                onClick={comingSoon ? undefined : () => navigate(locked ? `/kid/${childId}/demo` : `/kid/${childId}/subject/${encodeURIComponent(s.name)}`)}
                disabled={comingSoon}
                aria-disabled={comingSoon}
                className={`nb-card bg-white p-6 text-left relative overflow-hidden ${comingSoon ? "opacity-70 cursor-not-allowed" : ""}`}
                data-testid={comingSoon ? `kid-subject-soon-${s.name}` : `kid-subject-${s.name}`}
              >
                <div className="w-16 h-16 rounded-2xl border-4 border-slate-900 flex items-center justify-center mb-4" style={{ background: comingSoon ? "#94A3B8" : s.color }}>
                  <Icon color="#fff" size={30} />
                </div>
                <h3 className="text-xl font-bold" data-testid={`kid-subject-name-${s.name}`}>{subjectLabel(s.name)}</h3>
                {comingSoon ? (
                  <p className="font-parent text-sm text-slate-500 mt-1">Grade {child.grade} — coming soon</p>
                ) : (
                  <>
                    <p className="font-parent text-sm text-slate-500 mt-1">{s.completed_levels}/{s.total_levels} levels done</p>
                    <div className="mt-3 h-2.5 rounded-full bg-slate-100 overflow-hidden border border-slate-200">
                      <div className="h-full rounded-full" style={{ width: `${s.percent}%`, background: s.color }} />
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between mt-4">
                  {comingSoon ? (
                    <span className="inline-flex items-center gap-1 text-xs font-parent font-semibold text-slate-600 bg-slate-100 border border-slate-300 px-2 py-1 rounded-full">
                      <Construction size={12} /> Under construction
                    </span>
                  ) : locked ? (
                    <span className="inline-flex items-center gap-1 text-xs font-parent font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                      <Lock size={12} /> Free trial
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-parent font-semibold text-green-600 bg-green-50 border border-green-200 px-2 py-1 rounded-full">
                      Unlocked
                    </span>
                  )}
                  {!comingSoon && <ChevronRight size={20} className="text-slate-400" />}
                </div>
                {comingSoon ? (
                  <div className="absolute top-4 right-4">
                    <Construction size={18} className="text-slate-400" />
                  </div>
                ) : locked ? (
                  <div className="absolute top-4 right-4">
                    <Lock size={18} className="text-slate-300" />
                  </div>
                ) : null}
              </motion.button>
            );
          })}
        </div>

        {!plan_active && !readingOnly && (
          <div className="nb-card bg-blue-600 text-white p-6 mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="text-xl font-bold">Want more levels?</p>
              <p className="font-parent text-blue-100 text-sm mt-1">Ask a parent to unlock every chapter and subject.</p>
            </div>
            <Button onClick={() => navigate("/pricing")} className="rounded-full bg-yellow-300 text-slate-900 hover:bg-yellow-400 border-2 border-slate-900 font-parent font-semibold" data-testid="kid-upgrade">
              See plans
            </Button>
          </div>
        )}
      </div>
      <FloatingCalculator />
    </div>
  );
}

function Badge({ icon: Icon, color, value, testId }) {
  return (
    <div className="flex flex-col items-center" data-testid={testId}>
      <div className="w-12 h-12 rounded-full border-4 border-slate-900 flex items-center justify-center" style={{ background: color, boxShadow: "3px 3px 0 0 #0F172A" }}>
        <Icon color="#fff" size={20} fill="#fff" />
      </div>
      <span className="text-sm font-bold mt-1">{value}</span>
    </div>
  );
}

function StreakCalendar({ activity, streak }) {
  const DOW = ["S", "M", "T", "W", "T", "F", "S"];
  const byDate = {};
  (activity || []).forEach((a) => { if (a && a.date) byDate[a.date] = a.type; });
  const today = new Date();
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    days.push({ key, dow: DOW[d.getDay()], type: byDate[key], isToday: i === 0 });
  }
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="nb-card bg-white p-4 sm:p-5" data-testid="kid-streak-calendar">
      <div className="flex items-center justify-between mb-3">
        <p className="font-kids text-lg font-bold text-slate-900 flex items-center gap-2"><Flame className="text-orange-500" size={20} /> Your week</p>
        <span className="font-parent text-sm text-slate-500">{streak}-day streak</span>
      </div>
      <div className="flex justify-between gap-1.5">
        {days.map((d) => {
          const active = d.type === "active";
          const frozen = d.type === "frozen";
          return (
            <div key={d.key} className="flex flex-col items-center gap-1 flex-1" data-testid={`streak-day-${d.key}`}>
              <div
                className={`w-full aspect-square max-w-[52px] rounded-xl border-2 flex items-center justify-center ${d.isToday ? "border-slate-900" : "border-slate-200"} ${active ? "bg-orange-100" : frozen ? "bg-sky-100" : "bg-slate-50"}`}>
                {active ? <Flame className="text-orange-500" size={22} fill="currentColor" />
                  : frozen ? <Snowflake className="text-sky-500" size={22} />
                  : <span className="w-2 h-2 rounded-full bg-slate-300" />}
              </div>
              <span className={`text-xs font-parent ${d.isToday ? "font-bold text-slate-900" : "text-slate-400"}`}>{d.dow}</span>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}


function DemoBanner({ onStart }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="nb-card bg-yellow-200 p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="kid-demo-banner">
      <div className="w-12 h-12 rounded-full border-4 border-slate-900 bg-white flex items-center justify-center shrink-0">
        <Sparkles className="text-amber-600" size={24} />
      </div>
      <div className="flex-1">
        <p className="font-kids text-xl font-bold text-slate-900">10 minutes free, everything open!</p>
        <p className="font-parent text-sm text-slate-700">Ask your parent to start your free trial: every subject and the Reading kit for 10 minutes. / 10 min d'essai gratuit avec tout.</p>
      </div>
      <button onClick={onStart} data-testid="kid-demo-start"
        className="shrink-0 rounded-full bg-slate-900 text-white font-bold px-5 py-3 hover:bg-slate-800 transition-colors">
        Start the free trial
      </button>
    </motion.div>
  );
}
